import * as Sentry from "@sentry/vue";
import * as v from "valibot";
import { reactive, toRaw } from "vue";

import { defineStore } from "@/lib/depot";

import { useLocalStorageRef } from "@/lib/composables/local-storage";
import { LinkStatus } from "@/lib/link-checking/types";

const GOOD_LINK_EXPIRY = 5 * 24 * 60 * 60 * 1000; // 5 days
const BAD_LINK_EXPIRY = 60 * 60 * 1000; // 1 hour

const CLEANUP_DELAY = 5_000; // 5 seconds

const entrySchema = v.object({
  expires: v.pipe(
    v.number(),
    v.check((input) => {
      return !Number.isNaN(new Date(input).getTime());
    }, `must be a valid integer timestamp`)
  ),
  // For some reason, if I assign LinkStatus directly it's just `undefined`.
  status: v.union([
    v.literal<LinkStatus.OK>(1),
    v.literal<LinkStatus.NOT_OK>(2),
  ]),
});

type LinkEntry = v.InferOutput<typeof entrySchema>;
type EntryStatus = LinkEntry["status"];

const stateSchema = v.object({
  version: v.optional(v.literal(1), 1),
  map: v.record(v.string(), v.optional(entrySchema)),
});

type State = v.InferOutput<typeof stateSchema>;

const getDefaultState = (): State => {
  return {
    version: 1,
    map: {},
  };
};

export const useStore = defineStore("link_checker", () => {
  const state = useLocalStorageRef<State>("pinia:link-checker", {
    parse(raw) {
      if (raw !== null) {
        try {
          const parsed = JSON.parse(raw);
          return v.parse(stateSchema, parsed);
        } catch (err) {
          Sentry.captureException(err, {
            fingerprint: ["pinia", "link-checker", "restore-fail"],
          });
        }
      }

      return getDefaultState();
    },
    stringify(value) {
      return JSON.stringify(value);
    },
  });

  const getEntry = (url: string): LinkStatus | null => {
    if (isUnsafeKey(url)) {
      return null;
    }

    const entry = state.value.map[url];
    if (entry === undefined) {
      return null;
    }

    if (Date.now() > entry.expires) {
      delete state.value.map[url];

      return null;
    }

    return entry.status;
  };

  const setEntry = (url: string, status: EntryStatus): void => {
    if (isUnsafeKey(url)) {
      return;
    }

    const ms = status === LinkStatus.OK ? GOOD_LINK_EXPIRY : BAD_LINK_EXPIRY;

    state.value.map[url] = {
      status: status,
      expires: Date.now() + ms,
    };
  };

  // Check if there's anything we can purge once we know things have settled.
  setTimeout(() => {
    // Avoid updating the local storage unless we've actually changed something.
    let changed = false;

    const cloned = { ...toRaw(state.value.map) };
    const now = Date.now();
    for (const url in cloned) {
      const entry = cloned[url];

      if (entry === undefined) {
        // We'll try to remove this entry, but it's fine really if it exists.
        delete cloned[url];
      } else if (now > entry.expires) {
        delete cloned[url];
        changed = true;
      }
    }

    if (changed) {
      state.value.map = cloned;
    }
  }, CLEANUP_DELAY);

  return reactive({
    getEntry,
    setEntry,
  });
});

// This shouldn't be necessary, but just in case.
const isUnsafeKey = (key: string) => {
  return (
    key === "__proto__" ||
    key === "__defineGetter__" ||
    key === "__defineSetter__" ||
    key === "prototype" ||
    key === "constructor"
  );
};
